/** Macro #30 — Report + Block kebab menu (shared across comments/profiles/territory). */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cn } from "../cn";
import {
  socialApi, SocialApiError,
  type ReportReason, type ReportTargetType,
} from "../../lib/social-api";
import { useSession } from "../marketplace/useSession";
import { MoreIcon, Spinner } from "./social-ui";
import { useI18n } from "../../i18n";

const REASON_VALUES: ReportReason[] = [
  "spam", "abuse", "harassment", "hate", "nsfw", "scam", "impersonation", "other",
];

interface Props {
  targetType: ReportTargetType;
  targetId: string;
  /** Author wallet — enables Block. Omit to hide Block. */
  authorWallet?: string | null;
  testId?: string;
}

export function ReportMenu({ targetType, targetId, authorWallet, testId }: Props) {
  const { t } = useI18n();
  const { wallet, isAuthenticated } = useSession();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setReporting(false); }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const isSelf = !!authorWallet && !!wallet && authorWallet.toLowerCase() === wallet.toLowerCase();

  const reportMut = useMutation({
    mutationFn: (reason: ReportReason) => socialApi.report({ target_type: targetType, target_id: targetId, reason }),
    onSuccess: () => { setMsg(t("social.report.reported")); setReporting(false); setTimeout(() => setOpen(false), 900); },
    onError: (e) => setMsg(e instanceof SocialApiError ? e.message : t("social.report.reportFailed")),
  });

  const blockMut = useMutation({
    mutationFn: () => socialApi.block(authorWallet as string),
    onSuccess: () => {
      setMsg(t("social.report.blocked"));
      qc.invalidateQueries({ queryKey: ["social"] });
      setTimeout(() => setOpen(false), 900);
    },
    onError: (e) => setMsg(e instanceof SocialApiError ? e.message : t("social.report.blockFailed")),
  });

  if (!isAuthenticated) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={testId ?? "report-menu-btn"}
        aria-label={t("social.report.moreOptions")}
        onClick={() => { setOpen((v) => !v); setMsg(null); }}
        className="inline-flex h-6 w-6 items-center justify-center rounded-md text-white/45 hover:text-white hover:bg-white/[0.06] transition-colors"
      >
        <MoreIcon />
      </button>
      {open && (
        <div
          data-testid="report-menu-popover"
          role="menu"
          className={cn(
            "absolute right-0 mt-1 z-modal w-48 overflow-hidden rounded-xl",
            "border border-white/10 bg-[rgba(12,16,22,0.97)] backdrop-blur-xl shadow-[0_20px_50px_-18px_rgba(0,0,0,0.9)]",
          )}
        >
          {msg ? (
            <div className="px-3 py-3 text-[12px] text-white/70">{msg}</div>
          ) : reporting ? (
            <div className="py-1">
              <div className="px-3 py-2 font-mono text-[9.5px] uppercase tracking-[0.16em] text-white/45">{t("social.report.reason")}</div>
              {REASON_VALUES.map((value) => (
                <button
                  key={value}
                  type="button"
                  data-testid={`report-reason-${value}`}
                  disabled={reportMut.isPending}
                  onClick={() => reportMut.mutate(value)}
                  className="block w-full px-3 py-2 text-left text-[12px] text-white/80 hover:bg-white/[0.05]"
                >
                  {t(`social.report.reasons.${value}`)}
                </button>
              ))}
            </div>
          ) : (
            <div className="py-1">
              <button
                type="button"
                data-testid="report-open"
                onClick={() => setReporting(true)}
                className="block w-full px-3 py-2.5 text-left text-[12px] text-white/80 hover:bg-white/[0.05]"
              >
                {t("social.report.report")}
              </button>
              {authorWallet && !isSelf && (
                <button
                  type="button"
                  data-testid="block-btn"
                  disabled={blockMut.isPending}
                  onClick={() => blockMut.mutate()}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[12px] text-neon-coral hover:bg-white/[0.05]"
                >
                  {blockMut.isPending && <Spinner />} {t("social.report.blockUser")}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
